import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium " +
    "transition-[background,color,border,box-shadow] disabled:pointer-events-none " +
    "disabled:opacity-50 focus-visible:outline focus-visible:outline-2 " +
    "focus-visible:outline-offset-2 focus-visible:outline-ink",
  {
    variants: {
      variant: {
        primary: "bg-ink text-paper hover:bg-ink/90 active:bg-ink/95 " + "border border-ink",
        secondary: "bg-paper text-ink border border-line-strong hover:bg-paper-soft",
        ghost: "bg-transparent text-ink hover:bg-paper-soft",
        danger: "bg-danger text-paper hover:bg-danger/90 border border-danger",
      },
      size: {
        sm: "h-8 px-3 text-sm rounded-sm",
        md: "h-10 px-4 text-[15px] rounded-md",
        lg: "h-12 px-5 text-base rounded-md",
        xl: "h-14 px-6 text-lg rounded-md font-semibold",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = "Button";
